import React from 'react';

import { Button, Card, Modal, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { useSelector } from 'react-redux';

import usePreventBodyScroll from 'hooks/usePreventBodyScroll';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';

import { HighlightedContent } from 'components/HighlightedContent';

import './contact-us-modal.scss';

interface ContactUsModalRequiredProps {
  isOpen: boolean;
  toggleModal: () => void;
}

// Optional Props
interface ContactUsModalOptionalProps {
  showBanner?: boolean;
}

// Combined required and optional props to build the full prop interface
export interface ContactUsModalProps extends ContactUsModalRequiredProps, ContactUsModalOptionalProps {}

const ContactUsModal: React.FC<ContactUsModalProps> = ({ isOpen, toggleModal, showBanner }) => {
  const { t } = useTranslation();
  const device = useDevice();
  const contactEmail = useSelector(applicationStateSelectors.selectContactEmail);
  const contactPhone = useSelector(applicationStateSelectors.selectContactPhone);
  const handleMailTo = () => {
    window.location.href = `mailto:${contactEmail}`;
  };
  const handlePhone = () => {
    window.location.href = `tel:${contactPhone?.replaceAll('.', '').replaceAll(' ', '')}`;
  };

  usePreventBodyScroll(isOpen);

  return (
    <Modal className='contact-us-modal' isOpen={isOpen} onClose={toggleModal} closeOnBackDropClick disabledMobileAnimation>
      <Modal.Header onClose={toggleModal} closeIcon>
        <h3>{t('contact-us-modal.title')}</h3>
      </Modal.Header>
      {showBanner && (
        <HighlightedContent
          iconName='myhome-contact'
          title={t('card-apartment.highlighted-content.asset-type-room.init.title')}
          subtitle={t('card-apartment.highlighted-content.asset-type-room.init.subtitle')}
        />
      )}
      <Modal.Content>
        <Card fluid>
          <Card.Header>{t('contact-us-modal.email.title')}</Card.Header>
          <Card.Content>{t('contact-us-modal.email.description')}</Card.Content>
          <Card.Footer>
            <Button fluid iconName='mail' onClick={handleMailTo}>
              {t('contact-us-modal.email.button-label')}
            </Button>
          </Card.Footer>
        </Card>
        {contactPhone && (
          <Card fluid>
            <Card.Header>{t('contact-us-modal.phone.title')}</Card.Header>
            <Card.Content>{t('contact-us-modal.phone.description')}</Card.Content>
            <Card.Footer>
              <Button fluid outlined iconName='phone' onClick={handlePhone}>
                {device?.type === 'desktop' ? contactPhone : t('contact-us-modal.phone.button-label')}
              </Button>
            </Card.Footer>
          </Card>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default React.memo(ContactUsModal);
